import { useMemo } from 'react';
import { RuntimeFilterOp } from '@thoughtspot/visual-embed-sdk/react';
import { getInfinityListPropsFromQuery, MultiSelectWithQueriesUrl } from 'shared/ui/MultiSelect';
import { ContractorCampaignsPageFilter, useGetCampaignsInfinityList } from 'entities/campaign';
import { FILTER_STYLES } from 'shared/constants';
import { useGetServicesForFilters } from 'entities/service';
import { DatePickerWithQueriesUrl } from 'shared/ui/DatePicker';
import { ContractorReportsFilter } from 'entities/reports';

import {
  datepickerMaxDate,
  datepickerMinDate,
  getCampaignsOptionsNameIdFromInfinite,
  useGetQueryParams,
  useGetServicesOptionsAliasId,
} from '../utils';
import { LiveboardEmbedConstructor } from '../LiveboardEmbedConstructor';
import { NotAvailableBackgroundType } from '../NotAvailable';

const LIVEBOARD_ID = '12fec252-f55c-4209-abfb-b514ccdef378';
const selectedServicesQueryKey = ContractorReportsFilter.serviceIdArray;
const selectedCampaignsQueryKey = ContractorReportsFilter.campaignsIdArray;
const dateFromQueryKey = ContractorReportsFilter.from;
const dateToQueryKey = ContractorReportsFilter.to;

export function PerformanceMetricsReport() {
  const getCampaignsFiltersQuery = useGetCampaignsInfinityList({
    isSyncedWithUrl: false,
    filterName: ContractorCampaignsPageFilter.campaignsIdArray,
    getFieldValueToShow: (item) => item.name,
  });

  const campaignsOptions = useMemo(() => {
    return getCampaignsOptionsNameIdFromInfinite(getCampaignsFiltersQuery.data);
  }, [getCampaignsFiltersQuery.data?.pages]);

  const servicesOptions = useGetServicesOptionsAliasId();
  const servicesFiltersQuery = useGetServicesForFilters();

  const selectedServices = useGetQueryParams(selectedServicesQueryKey);
  const selectedCampaigns = useGetQueryParams(selectedCampaignsQueryKey);

  const dateFrom = useGetQueryParams(dateFromQueryKey);
  const dateTo = useGetQueryParams(dateToQueryKey);

  const getRuntimeFilters = () => {
    const runtimeFilters = [];

    if (selectedServices.length && selectedServices.length < servicesOptions.length) {
      runtimeFilters.push({
        columnName: 'Service Alias',
        operator: RuntimeFilterOp.IN,
        values: selectedServices,
      });
    }

    if (selectedCampaigns.length && selectedCampaigns.length < campaignsOptions.length) {
      runtimeFilters.push({
        columnName: 'Campaign Name',
        operator: RuntimeFilterOp.IN,
        values: selectedCampaigns,
      });
    }

    if (dateFrom.length) {
      runtimeFilters.push({
        columnName: 'Date',
        operator: RuntimeFilterOp.GE,
        values: [(new Date(dateFrom[0]).getTime() / 1000).toString()],
      });
    }

    if (dateTo.length) {
      runtimeFilters.push({
        columnName: 'Date',
        operator: RuntimeFilterOp.LE,
        values: [(new Date(dateTo[0]).getTime() / 1000).toString()],
      });
    }

    return runtimeFilters;
  };

  const runtimeFilters = getRuntimeFilters();

  return (
    <LiveboardEmbedConstructor
      type="disposition"
      filters={
        <>
          <MultiSelectWithQueriesUrl.Search
            rootClassName={`${FILTER_STYLES.select} min-w-[345px]`}
            title="Service"
            options={servicesOptions}
            disabled={servicesFiltersQuery.isLoading}
            loading={servicesFiltersQuery.isLoading}
            filterName={selectedServicesQueryKey}
            isWhite
          />
          <MultiSelectWithQueriesUrl.InfinityList
            {...getInfinityListPropsFromQuery(getCampaignsFiltersQuery)}
            rootClassName={`${FILTER_STYLES.select} min-w-[345px]`}
            title="Campaign name"
            key="Campaign"
            options={campaignsOptions}
            filterName={selectedCampaignsQueryKey}
            isWhite
          />
          <DatePickerWithQueriesUrl
            fromKey={dateFromQueryKey}
            toKey={dateToQueryKey}
            wrapperClass={FILTER_STYLES.datePicker}
            isWhite
            initialFrom={datepickerMinDate()}
            initialTo={datepickerMaxDate()}
            minDate={datepickerMinDate()}
            maxDate={datepickerMaxDate()}
          />
        </>
      }
      title="Performance metrics"
      liveBoardId={LIVEBOARD_ID}
      runtimeFilters={runtimeFilters}
      notAvailableBackgroundType={NotAvailableBackgroundType.performanceMetricsBackground}
    />
  );
}
